import { useMemo } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { pdfjs } from "react-pdf";
import AssessmentCenterImpactDataGrid from "./AssessmentCenterImpactDataGrid";
import {
  createAiResultsModalColumns,
  type AiResultsModalRow,
} from "./assessmentCenterColumns";
import {
  ASSESSMENT_IMPACT_DIALOG_BODY_TOP_OFFSET_SX,
  ASSESSMENT_IMPACT_DIALOG_CONTENT_SX,
  ASSESSMENT_IMPACT_DIALOG_TITLE_CLOSE_BUTTON_SX,
  ASSESSMENT_IMPACT_DIALOG_TITLE_DESC_SX,
  ASSESSMENT_IMPACT_DIALOG_TITLE_ROW_SX,
  ASSESSMENT_IMPACT_DIALOG_TITLE_TEXT_SX,
} from "./assessmentImpactDialogTitleStyles";
import { useAiImpactAssessmentRowActions } from "./useAiImpactAssessmentRowActions";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { PDFViewer } from "../RegulationDetails/PDFViewer";
import { ELEVATED_DIALOG_Z_INDEX_OFFSET } from "../../constants/dialogZIndex";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import { SelectPlanDialogIcon } from "./SelectPlanDialogIcon";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type { AiResultsModalRow };

export type AIImpactAssessmentResultsModalProps = Readonly<{
  open: boolean;
  rows: AiResultsModalRow[];
  loading?: boolean;
  onClose: () => void;
  onOpenAssessmentCenter?: () => void;
  onStartManualAssessment?: (row: AiResultsModalRow) => void;
  /** Called after a successful re-run (e.g. resume regulation-status polling). */
  onRetrySuccess?: (row: AiResultsModalRow) => void;
}>;

export default function AIImpactAssessmentResultsModal({
  open,
  rows,
  loading = false,
  onClose,
  onOpenAssessmentCenter,
  onStartManualAssessment,
  onRetrySuccess,
}: AIImpactAssessmentResultsModalProps) {
  const canRerunSystem = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  const canExecuteManualAssessment = useHasPrivilege(
    "gri.assessment.manual.execute",
  );

  const {
    actionHandlers,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog,
  } = useAiImpactAssessmentRowActions({
    canRerunSystem,
    onRetrySuccess: onRetrySuccess
      ? (actionRow) => {
          const fullRow = rows.find(
            (r) =>
              r.regulationId === actionRow.regulationId &&
              r.version === actionRow.version,
          );
          if (fullRow) onRetrySuccess(fullRow);
        }
      : undefined,
  });

  const columns = useMemo(() => {
    const {
      onStartManualAssessment: _omitManual,
      ...aiRowActionHandlers
    } = actionHandlers;
    return createAiResultsModalColumns({
      ...aiRowActionHandlers,
      onCloseModal: onClose,
      onStartManualAssessment:
        canExecuteManualAssessment && onStartManualAssessment
          ? onStartManualAssessment
          : undefined,
    });
  }, [
    actionHandlers,
    canExecuteManualAssessment,
    onClose,
    onStartManualAssessment,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(onClose)}
        maxWidth={false}
        fullWidth
        slotProps={{
          root: {
            sx: {
              zIndex: (theme) =>
                theme.zIndex.modal + ELEVATED_DIALOG_Z_INDEX_OFFSET,
            },
          },
          paper: {
            sx: {
              borderRadius: "12px",
              width: 1300,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "92vh",
            },
          },
        }}
      >
        <DialogTitle component="div" sx={ASSESSMENT_IMPACT_DIALOG_TITLE_ROW_SX}>
          <SelectPlanDialogIcon />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignSelf: "center",
            }}
          >
            <Typography component="span" sx={ASSESSMENT_IMPACT_DIALOG_TITLE_TEXT_SX}>
              AI Impact Assessment Results
            </Typography>
            <Typography component="span" sx={ASSESSMENT_IMPACT_DIALOG_TITLE_DESC_SX}>
              You can also find the result in{" "}
              {onOpenAssessmentCenter ? (
                <Link
                  component="button"
                  underline="hover"
                  onClick={onOpenAssessmentCenter}
                  sx={{
                    font: "inherit",
                    verticalAlign: "baseline",
                    color: "primary.main",
                  }}
                >
                  Assessment Center
                </Link>
              ) : (
                "Assessment Center"
              )}
              .
            </Typography>
          </Box>
          <IconButton
            aria-label="Close AI Impact Assessment Results"
            onClick={onClose}
            sx={ASSESSMENT_IMPACT_DIALOG_TITLE_CLOSE_BUTTON_SX}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={ASSESSMENT_IMPACT_DIALOG_CONTENT_SX}>
          <Box sx={ASSESSMENT_IMPACT_DIALOG_BODY_TOP_OFFSET_SX}>
            <AssessmentCenterImpactDataGrid
              embedded
              ariaLabel="AI impact assessment results"
              rows={rows}
              loading={loading}
              columns={columns}
              height={420}
              actionCellOverflowVisible
            />
          </Box>
        </DialogContent>
      </Dialog>

      <PDFViewer
        open={pdfDialogOpen}
        pdfUrl={selectedPdfUrl}
        title="PDF Preview"
        onClose={closePdfDialog}
        stackAboveElevatedModal
      />
    </>
  );
}
